import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Badge,
} from "reactstrap";
import { isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";
import { unassignLicenseFromUserApi } from "../../../../helpers/fakebackend_helper";

import TableContainer from "../../../../Components/Common/TableContainer";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import HandleDateTime from "../../../../Components/Common/HandleDateTime";
import { HandleDateTimeAgo } from "../../../../Components/Common/HandleDateTimeAgo";
import SettingsPanel from "../../components/SettingsPanel";
import SignInLogModal from "./SignInLogModal";
import { SettingsSubTabContext } from "../../settingsTabContext";
import CreateEditCompanyUserLicenses from "../../../../Components/popUpComponents/CreateEditCompanyUserLicenses";
import BulkAssignLicence from "../../../../Components/popUpComponents/AssignLicence/BulkAssignLicence";
import { isLivePaid, licenceState } from "../../../../Components/popUpComponents/AssignLicence/licenceState";
import {
  getCompanyTeamMembersList,
  companyToUserLockUser,
  companyToUserLockUserSuccess,
  companyToUserUnlockUser,
  companyToUserUnlockUserSuccess,
  companyToUserResetPassword,
  companyToUserResetPasswordSuccess,
  getUserAvailableDashboard,
} from "../../../../store/companyToUser/action";
import {
  getMenuNavigationList,
  getAvailableMenuNavigationItems,
} from "../../../../store/navigationMenu/action";

// Is an admin (SecurityAuthRole 100)? Password reset is administrator-only, so
// the button is hidden outright for everyone else. Same check the Billing intro
// panel and settingsNavDataCompany use.
const isAdministrator = () =>
  !!JSON.parse(localStorage.getItem("authUser"))?.get_securityauthrole_list?.find(
    (item) => item.SecurityAuthRole_Id === "100"
  );

// Change #100463 - the licences that give this person access today. The
// team list returns each person's licences (not ended); the access check
// also needs them started and inside the renewal grace.
const liveLicences = (row) =>
  (row?.subscriptionlicence_list || []).filter((l) =>
    isLivePaid({
      StartDateTime: l.SubscriptionLicence_StartDateTime,
      RenewalDateTime: l.SubscriptionLicence_RenewalDateTime,
      RenewalGraceDateTime: l.SubscriptionLicence_RenewalGraceDateTime,
      EndDateTime: l.SubscriptionLicence_EndDateTime,
    })
  );

// Change #100463 - adminCompanyId: Back End > Company Details > Users renders
// this same screen for another company (CRMit only). The list, lock, unlock,
// reset password and licence assignment then go through the developer-only
// .../admin/... endpoints for that company instead of the signed-in one. The
// Add New User wizard and Technical view are company-scoped, so they are
// hidden there.
export default function Users({ adminCompanyId = null, initialTab = null } = {}) {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // Change #100337 - "a toggle to switch to technical view": adds each
  // person's landing page, menu and dashboard. Remembered per browser.
  const [techView, setTechView] = useState(() => {
    try {
      return localStorage.getItem("usersTechnicalView") === "1";
    } catch (_) {
      return false;
    }
  });
  const toggleTechView = (on) => {
    setTechView(on);
    try {
      localStorage.setItem("usersTechnicalView", on ? "1" : "0");
    } catch (_) {
      // ignore storage errors
    }
  };
  const { dashboardTemplates, menuNavs, landingPages } = useSelector(
    (state) => ({
      dashboardTemplates:
        state.CompanyToUser?.userAvailableDashboard?.dashboardtemplate_list || [],
      menuNavs: state.NavigationMenu?.menuNavigationList?.menunav_list || [],
      landingPages:
        state.NavigationMenu?.availableMenuNavigationItems?.menunavoption_list ||
        [],
    })
  );
  useEffect(() => {
    if (!techView) return;
    dispatch(getAvailableMenuNavigationItems());
    if (adminCompanyId) return;
    dispatch(getUserAvailableDashboard());
    dispatch(getMenuNavigationList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [techView]);
  const findById = (list, id) =>
    (list || []).find((row) => String(row?.Id) === String(id));

  // Task 24441 - the same Add New User wizard the User Invitation panel opens.
  // This screen listed users but gave no way to add one, so adding a colleague
  // meant knowing to go to a different Billing tab.
  const [modalUserCreateEdit, setModalUserCreateEdit] = useState(false);

  // Reset-password modal: the row we are resetting. The link is always emailed
  // to the user and never shown here (Change #100329).
  const [resetTarget, setResetTarget] = useState(null);

  // Change #100463 - the person being given a licence.
  const [assignTarget, setAssignTarget] = useState(null);
  // Change #100463 - Disable = this person has LEFT (Staff archived, hidden
  // from task assignment and every staff dropdown). Enable reverses it.
  // { row, active: bool } being changed, confirm first.
  const [activeTarget, setActiveTarget] = useState(null);
  // Change #100668 - the user whose sign-in log is open.
  const [signInLogTarget, setSignInLogTarget] = useState(null);
  const [activeSaving, setActiveSaving] = useState(false);
  const setStaffActive = async (row, active) => {
    const body = [{ User_Id: Number(row.User_Id), FlagActive: active ? 1 : 0 }];
    const res = adminCompanyId
      ? await FetchAPI(`/companytouser/admin/setactive/${adminCompanyId}`, "POST", body)
      : await FetchAPI("/companytouser/setactive", "POST", body);
    if (Number(res?.status) !== 200) throw new Error(res?.message || "Could not update the user");
  };
  const confirmActive = async () => {
    if (!activeTarget) return;
    setActiveSaving(true);
    try {
      await setStaffActive(activeTarget.row, activeTarget.active);
      toast.success(
        activeTarget.active
          ? "User enabled. Assign them a licence so they can use the system."
          : "User disabled and their licences unassigned. They no longer appear in staff lists."
      );
      setActiveTarget(null);
      reloadList();
    } catch (e) {
      toast.error(e?.message || "Could not update the user");
    } finally {
      setActiveSaving(false);
    }
  };
  // Unassign: also disable them when they have left.
  const [unassignLeft, setUnassignLeft] = useState(false);

  // Change #100463 - { row, licence } being unassigned (confirm first).
  const [unassignTarget, setUnassignTarget] = useState(null);
  const [unassigning, setUnassigning] = useState(false);
  const confirmUnassign = async () => {
    const id = unassignTarget?.licence?.SubscriptionLicence_Id;
    if (!id) return;
    setUnassigning(true);
    try {
      const body = [{ Id: Number(id) }];
      const res = adminCompanyId
        ? await FetchAPI(`/subscriptionlicence/admin/unassign/company/${adminCompanyId}`, "POST", body)
        : await unassignLicenseFromUserApi(body);
      if (Number(res?.status) === 200) {
        if (unassignLeft && unassignTarget?.row) {
          try {
            await setStaffActive(unassignTarget.row, false);
          } catch (e) {
            toast.error(e?.message || "Licence unassigned, but could not disable the user");
          }
        }
        toast.success(
          unassignLeft
            ? "Licence unassigned and the user disabled."
            : "Licence unassigned. It is spare again and can be given to someone else."
        );
        setUnassignTarget(null);
        setUnassignLeft(false);
        reloadList();
      } else {
        toast.error(res?.message || "Could not unassign the licence");
      }
    } catch (e) {
      toast.error("Could not unassign the licence");
    } finally {
      setUnassigning(false);
    }
  };
  // Change #100463 - people ticked for "Assign licences". Anyone can be
  // ticked: a person can hold several licences (e.g. a seat and a SIM), just
  // not two of the same product; the popup skips those who already have it.
  const [selected, setSelected] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const selectableRef = useRef([]);
  const toggleSelected = (row) =>
    setSelected((cur) => {
      const next = { ...cur };
      const k = String(row.User_Id);
      if (next[k]) delete next[k];
      else next[k] = row;
      return next;
    });

  const authUser = JSON.parse(localStorage.getItem("authUser"));
  // Staff.Id == User.Id, so the logged-in staff id is our own User_Id.
  const currentUserId = authUser?.get_staff_data?.Staff_Id;
  const admin = useMemo(() => (adminCompanyId ? true : isAdministrator()), [adminCompanyId]);

  const {
    companyTeamMembersList,
    companyTeamMembersListLoading,
    companyToUserLockUserAPI,
    companyToUserUnlockUserAPI,
    companyToUserResetPasswordAPI,
    companyToUserResetPasswordLoading,
  } = useSelector((state) => ({
    companyTeamMembersList:
      state.CompanyToUser.companyTeamMembersList?.companytouser_list,
    companyTeamMembersListLoading:
      state.CompanyToUser.companyTeamMembersListLoading,
    companyToUserLockUserAPI: state.CompanyToUser.companyToUserLockUser,
    companyToUserUnlockUserAPI: state.CompanyToUser.companyToUserUnlockUser,
    companyToUserResetPasswordAPI:
      state.CompanyToUser.companyToUserResetPassword,
    companyToUserResetPasswordLoading:
      state.CompanyToUser.companyToUserResetPasswordLoading,
  }));

  const [adminList, setAdminList] = useState([]);
  const [adminLoading, setAdminLoading] = useState(!!adminCompanyId);
  const [adminResetLoading, setAdminResetLoading] = useState(false);
  const loadAdminList = async () => {
    setAdminLoading(true);
    try {
      const res = await FetchAPI(`/companytouser/admin/list/team/${adminCompanyId}`, "GET");
      setAdminList(Number(res?.status) === 200 ? res?.companytouser_list || [] : []);
      if (Number(res?.status) !== 200) toast.error(res?.message || "Could not load the users");
    } catch (e) {
      setAdminList([]);
    } finally {
      setAdminLoading(false);
    }
  };
  const reloadList = () => (adminCompanyId ? loadAdminList() : dispatch(getCompanyTeamMembersList()));
  const teamList = adminCompanyId ? adminList : companyTeamMembersList;
  const teamListLoading = adminCompanyId ? adminLoading : companyTeamMembersListLoading;
  const resetLoading = adminCompanyId ? adminResetLoading : companyToUserResetPasswordLoading;

  useEffect(() => {
    reloadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminCompanyId]);

  // The wizard refreshes getCompanyToUserListTeam on success, which is a
  // different store key from the one this screen reads - so a user added here
  // would not appear until a reload. Refetch whenever the wizard closes; doing
  // it on cancel too is a wasted call but never a stale list.
  const wizardWasOpen = useRef(false);
  useEffect(() => {
    if (wizardWasOpen.current && !modalUserCreateEdit) {
      dispatch(getCompanyTeamMembersList());
    }
    wizardWasOpen.current = modalUserCreateEdit;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalUserCreateEdit]);

  const handleToggleLock = (row) => {
    const isLocked = Number(row?.CompanyToUser_UserLockedFlag) === 1;
    const payload = [
      {
        CompanyToUser_Id: row?.CompanyToUser_Id,
        User_Id: row?.User_Id,
      },
    ];
    if (adminCompanyId) {
      FetchAPI(`/companytouser/admin/${isLocked ? "unlockuser" : "lockuser"}/${adminCompanyId}`, "POST", payload)
        .then((res) => {
          if (Number(res?.status) === 200) {
            toast.success(res?.message || (isLocked ? "User unlocked" : "User locked"));
            reloadList();
          } else {
            toast.error(res?.message || "Could not update the user");
          }
        })
        .catch(() => toast.error("Could not update the user"));
      return;
    }
    if (isLocked) {
      dispatch(companyToUserUnlockUser(payload));
    } else {
      dispatch(companyToUserLockUser(payload));
    }
  };

  const openResetModal = (row) => {
    setResetTarget(row);
  };

  const closeResetModal = () => {
    setResetTarget(null);
  };

  const handleConfirmReset = () => {
    if (adminCompanyId) {
      setAdminResetLoading(true);
      FetchAPI(`/companytouser/admin/resetpassword/${adminCompanyId}`, "POST", [
        { CompanyToUser_Id: resetTarget?.CompanyToUser_Id, User_Id: resetTarget?.User_Id, FlagSendEmail: 1 },
      ])
        .then((res) => {
          if (Number(res?.status) === 200) {
            const result = res?.companytouserresetpassword_list?.[0];
            toast.success(`Password reset email sent to ${result?.User_Email || resetTarget?.User_Email}`);
            closeResetModal();
          } else {
            toast.error(res?.message || "Could not reset password");
          }
        })
        .catch(() => toast.error("Could not reset password"))
        .finally(() => setAdminResetLoading(false));
      return;
    }
    dispatch(
      companyToUserResetPassword([
        {
          CompanyToUser_Id: resetTarget?.CompanyToUser_Id,
          User_Id: resetTarget?.User_Id,
          FlagSendEmail: 1,
        },
      ])
    );
  };

  // React to lock result, then refresh the list.
  useEffect(() => {
    if (isEmpty(companyToUserLockUserAPI)) return;
    if (companyToUserLockUserAPI.status === 200) {
      toast.success(companyToUserLockUserAPI.message || "User locked");
      dispatch(getCompanyTeamMembersList());
    } else {
      toast.error(companyToUserLockUserAPI.message || "Could not lock user");
    }
    dispatch(companyToUserLockUserSuccess([]));
  }, [companyToUserLockUserAPI]);

  // React to unlock result, then refresh the list.
  useEffect(() => {
    if (isEmpty(companyToUserUnlockUserAPI)) return;
    if (companyToUserUnlockUserAPI.status === 200) {
      toast.success(companyToUserUnlockUserAPI.message || "User unlocked");
      dispatch(getCompanyTeamMembersList());
    } else {
      toast.error(
        companyToUserUnlockUserAPI.message || "Could not unlock user"
      );
    }
    dispatch(companyToUserUnlockUserSuccess([]));
  }, [companyToUserUnlockUserAPI]);

  // React to the reset result: the link has been emailed, so we are done.
  useEffect(() => {
    if (isEmpty(companyToUserResetPasswordAPI)) return;
    if (companyToUserResetPasswordAPI.status === 200) {
      const result =
        companyToUserResetPasswordAPI.companytouserresetpassword_list?.[0];
      toast.success(`Password reset email sent to ${result?.User_Email}`);
      closeResetModal();
    } else {
      toast.error(
        companyToUserResetPasswordAPI.message || "Could not reset password"
      );
    }
    dispatch(companyToUserResetPasswordSuccess([]));
  }, [companyToUserResetPasswordAPI]);

  const columns = useMemo(
    () => [
      {
        // Change #100463 - tick people with no licence to assign in one go.
        id: "select",
        Header: () => {
          const rows = selectableRef.current || [];
          const all = rows.length > 0 && rows.every((r) => selected[String(r.User_Id)]);
          return (
            <Input
              type="checkbox"
              className="form-check-input"
              title="Tick everyone on this tab"
              disabled={!rows.length}
              checked={all}
              onChange={() =>
                setSelected((cur) => {
                  const next = { ...cur };
                  rows.forEach((r) => {
                    if (all) delete next[String(r.User_Id)];
                    else next[String(r.User_Id)] = r;
                  });
                  return next;
                })
              }
            />
          );
        },
        filterable: false,
        disableSortBy: true,
        Cell: (card) => {
          const row = card.row.original;
          return (
            <Input
              type="checkbox"
              className="form-check-input"
              checked={!!selected[String(row.User_Id)]}
              onChange={() => toggleSelected(row)}
            />
          );
        },
      },
      {
        Header: "Id",
        accessor: "User_Id",
        filterable: false,
        Cell: (card) => (
          <Link
            to={`/staff-details/${card.row.original?.User_Id}/view`}
            title="View user details"
          >
            <span className="like-href fw-medium">
              #{card.row.original?.User_Id}
            </span>
          </Link>
        ),
      },
      {
        Header: "Full Name",
        accessor: "User_UserFullName",
        filterable: true,
        // Change #100330 - A-Z ignoring case, so "amy" does not sort after "Zoe".
        sortType: (a, b) =>
          String(a.original?.User_UserFullName || "").localeCompare(
            String(b.original?.User_UserFullName || ""),
            undefined,
            { sensitivity: "base", numeric: true }
          ),
        Cell: (card) => <>{card.row.original?.User_UserFullName || ""}</>,
      },
      {
        Header: "Email",
        accessor: "User_Email",
        filterable: false,
        Cell: (card) => <span>{card.row.original?.User_Email}</span>,
      },
      {
        // Change #100334 - last sign-in to THIS company (CompanyToUser),
        // with "how long ago" underneath. Hover shows last activity too.
        Header: "Last Login",
        accessor: "CompanyToUser_LastLoginDateTime",
        filterable: false,
        sortType: (a, b) =>
          String(a.original?.CompanyToUser_LastLoginDateTime || "").localeCompare(
            String(b.original?.CompanyToUser_LastLoginDateTime || "")
          ),
        Cell: (card) => {
          const last = card.row.original?.CompanyToUser_LastLoginDateTime;
          const active = card.row.original?.CompanyToUser_LastActiveDateTime;
          if (!last) return <span className="text-muted">Never</span>;
          return (
            <div
              title={active ? `Last active ${HandleDateTime(active)}` : undefined}
            >
              <div>{HandleDateTime(last)}</div>
              <div className="text-muted fs-11">{HandleDateTimeAgo(last)}</div>
            </div>
          );
        },
      },
      {
        // Change #100463 - this read SubscriptionProduct_Name, which the team
        // list stopped returning, so it said "—" for everyone. It now shows
        // the licences that give access today, or that they have none.
        Header: "Licence",
        accessor: "SubscriptionProduct_Name",
        filterable: false,
        Cell: (card) => {
          // Every licence they hold that has not ended, so this matches the
          // Licences tab. One past its renewal grace (not renewed) no longer
          // gives access, so it shows red "lapsed" rather than vanishing.
          const held = card.row.original?.subscriptionlicence_list || [];
          if (!held.length) {
            return (
              <Badge color="" className="badge-soft-warning" title="In the company but cannot use the system until a licence is assigned">
                <i className="ri-error-warning-line align-bottom me-1"></i>No licence
              </Badge>
            );
          }
          return (
            <div className="d-flex flex-wrap gap-1">
              {held.map((l) => {
                const state = licenceState({
                  StartDateTime: l.SubscriptionLicence_StartDateTime,
                  RenewalDateTime: l.SubscriptionLicence_RenewalDateTime,
                  RenewalGraceDateTime: l.SubscriptionLicence_RenewalGraceDateTime,
                  EndDateTime: l.SubscriptionLicence_EndDateTime,
                });
                const lapsed = state === "lapsed";
                return (
                  <Badge
                    key={l.SubscriptionLicence_Id}
                    color=""
                    className={lapsed ? "badge-soft-danger" : state === "grace" ? "badge-soft-warning" : state === "future" ? "badge-soft-info" : "badge-soft-success"}
                    title={
                      lapsed
                        ? `Renewal was due ${String(l.SubscriptionLicence_RenewalDateTime || "").slice(0, 10)} and has not been paid, so it no longer gives access`
                        : state === "grace"
                        ? "Renewal due: still works until the grace period ends"
                        : undefined
                    }
                  >
                    {l.SubscriptionProduct_Name || `Licence #${l.SubscriptionLicence_Id}`}
                    {lapsed ? " · lapsed" : state === "grace" ? " · renewal due" : state === "future" ? " · starts later" : ""}
                    <button
                      type="button"
                      className="btn btn-link p-0 ms-1 align-baseline text-reset"
                      style={{ lineHeight: 1, fontSize: "inherit" }}
                      title="Unassign this licence"
                      onClick={() => {
                        setUnassignLeft(false);
                        setUnassignTarget({ row: card.row.original, licence: l });
                      }}
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </Badge>
                );
              })}
            </div>
          );
        },
      },
      {
        Header: "Security Roles",
        filterable: false,
        Cell: (card) => {
          const roles = card.row.original?.securityauthrole_list || [];
          if (!roles.length) {
            return <span className="text-muted">No roles</span>;
          }
          return (
            <div className="d-flex flex-wrap gap-1">
              {roles.map((r) => (
                <Badge
                  key={r.SecurityAuthRoleToUser_Id || r.SecurityAuthRole_Id}
                  color="primary"
                  className="bg-primary-subtle text-primary"
                  title={r.SecurityAuthRole_Description || ""}
                >
                  {r.SecurityAuthRole_Name || `Role #${r.SecurityAuthRole_Id}`}
                </Badge>
              ))}
            </div>
          );
        },
      },
      // Change #100337 - technical view: what they get when they sign in.
      // These are the Staff fields AuthLoginController uses, resolved to names
      // from the same lists the Edit Staff form picks from.
      ...(techView
        ? [
            {
              Header: "Landing Page",
              accessor: "Staff_DefaultOpeningNavMenu",
              filterable: false,
              Cell: (card) => {
                const id = card.row.original?.Staff_DefaultOpeningNavMenu;
                if (!id) return <span className="text-muted">Company default</span>;
                const page = findById(landingPages, id);
                return (
                  <div>
                    <div>{page?.Name || `#${id}`}</div>
                    {page?.Url && (
                      <div className="text-muted fs-11">/{String(page.Url).replace(/^\//, "")}</div>
                    )}
                  </div>
                );
              },
            },
            {
              Header: "Menu",
              accessor: "Staff_MenuNav_Id",
              filterable: false,
              Cell: (card) => {
                const id = card.row.original?.Staff_MenuNav_Id;
                if (!id) return <span className="text-muted">Company default</span>;
                return <span>{findById(menuNavs, id)?.Name || card.row.original?.Staff_MenuNav_Name || `#${id}`}</span>;
              },
            },
            {
              Header: "Dashboard",
              accessor: "Staff_DashboardTemplate_Id",
              filterable: false,
              Cell: (card) => {
                const id = card.row.original?.Staff_DashboardTemplate_Id;
                if (!id) return <span className="text-muted">Company default</span>;
                return <span>{findById(dashboardTemplates, id)?.Name || card.row.original?.Staff_DashboardTemplate_Name || `#${id}`}</span>;
              },
            },
          ]
        : []),
      {
        Header: "Status",
        accessor: "CompanyToUser_UserLockedFlag",
        filterable: false,
        Cell: (card) => {
          const isLocked =
            Number(card.row.original?.CompanyToUser_UserLockedFlag) === 1;
          // Change #100463 - when they were locked, under the badge.
          const lockedAt = card.row.original?.CompanyToUser_UserLockedDateTime;
          // Change #100463 - Status matches the tabs (Active / Inactive /
          // Disabled), with Locked shown as well when their login is locked.
          // It used to show only the lock, so a disabled person read "Active"
          // next to an Enable button.
          const group = groupOf(card.row.original);
          const groupBadge =
            group === "disabled" ? (
              <Badge color="secondary" className="text-uppercase" title="Has left: hidden from staff lists">
                <i className="ri-user-unfollow-line align-bottom me-1"></i>Disabled
              </Badge>
            ) : group === "inactive" ? (
              <Badge color="warning" className="text-uppercase" title="No working licence">
                <i className="ri-key-2-line align-bottom me-1"></i>Inactive
              </Badge>
            ) : (
              <Badge color="success" className="text-uppercase">
                <i className="ri-shield-check-line align-bottom me-1"></i>Active
              </Badge>
            );
          return (
            <div>
              <div className="d-flex flex-wrap gap-1">
                {groupBadge}
                {isLocked && (
                  <Badge color="danger" className="text-uppercase">
                    <i className="ri-lock-line align-bottom me-1"></i>Locked
                  </Badge>
                )}
              </div>
              {isLocked && lockedAt && (
                <div className="text-muted fs-11 mt-1" title={HandleDateTimeAgo(lockedAt)}>
                  Locked {HandleDateTime(lockedAt)}
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: "Action",
        filterable: false,
        Cell: (card) => {
          const row = card.row.original;
          const isLocked = Number(row?.CompanyToUser_UserLockedFlag) === 1;
          const isSelf =
            currentUserId &&
            row?.User_Id?.toString() === currentUserId?.toString();
          return (
            <div className="hstack gap-2">
              {/* Change #100668 - when, where and on which device they signed in. */}
              <button
                type="button"
                title="When, where and on which device they signed in to this company"
                className="btn btn-sm btn-soft-dark"
                onClick={() => setSignInLogTarget(row)}
              >
                <i className="ri-history-line align-bottom me-1"></i>
                Sign-ins
              </button>
              <button
                type="button"
                title="Give this person a licence (another licence type if they already have one)"
                className={`btn btn-sm ${liveLicences(row).length === 0 ? "btn-success" : "btn-soft-success"}`}
                onClick={() => setAssignTarget(row)}
              >
                <i className="ri-key-2-line align-bottom me-1"></i>
                Assign licence
              </button>
              <button
                type="button"
                disabled={isSelf}
                title={
                  isSelf
                    ? "You cannot lock your own account"
                    : isLocked
                    ? "Unlock this user"
                    : "Lock this user"
                }
                className={`btn btn-sm ${
                  isLocked ? "btn-soft-success" : "btn-soft-danger"
                }`}
                onClick={() => handleToggleLock(row)}
              >
                <i
                  className={`align-bottom me-1 ${
                    isLocked ? "ri-lock-unlock-line" : "ri-lock-line"
                  }`}
                ></i>
                {isLocked ? "Unlock" : "Lock"}
              </button>
              {admin && (
                <button
                  type="button"
                  title="Send this user a password reset link"
                  className="btn btn-sm btn-soft-primary"
                  onClick={() => openResetModal(row)}
                >
                  <i className="ri-key-2-line align-bottom me-1"></i>
                  Reset Password
                </button>
              )}
              {groupOf(row) === "disabled" ? (
                <button
                  type="button"
                  title="They are back: show them in staff lists again"
                  className="btn btn-sm btn-soft-success"
                  onClick={() => setActiveTarget({ row, active: true })}
                >
                  <i className="ri-user-follow-line align-bottom me-1"></i>
                  Enable
                </button>
              ) : (
                <button
                  type="button"
                  disabled={isSelf}
                  title={isSelf ? "You cannot disable yourself" : "They have left: hide them from task assignment and staff lists"}
                  className="btn btn-sm btn-soft-secondary"
                  onClick={() => setActiveTarget({ row, active: false })}
                >
                  <i className="ri-user-unfollow-line align-bottom me-1"></i>
                  Disable
                </button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUserId, admin, techView, landingPages, menuNavs, dashboardTemplates, selected]
  );

  const allowedSearchFilters = [
    "User_Id",
    "User_UserFullName",
    "User_UserLastName",
    "User_Email",
    "SubscriptionProduct_Name",
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      const filtered = teamList?.filter((item) =>
        allowedSearchFilters.some(
          (key) =>
            item[key] &&
            item[key]
              .toString()
              .toLowerCase()
              .includes(search?.toLowerCase())
        )
      );
      setSearchResults(filtered);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, teamList]);

  // Change #100331 - Active / Locked / Disabled tabs with a count on each.
  // Disabled = the staff record is marked inactive (Staff.FlagActive = 0, a
  // leaver); it wins over Locked so a person is only ever in one tab. Locked =
  // this company's login is locked. Everyone else is Active.
  // Change #100463 (Paul) - tabs, each its own URL (/settings/4/6/active,
  // /company-details/{id}/Users/active):
  //   All      - everyone,
  //   Active   - staff with a working licence (locked people stay here with
  //              their Locked badge; the Locked toggle narrows to them),
  //   Inactive - still staff (still in dropdowns) but no working licence,
  //   Disabled - has LEFT: Staff.FlagActive = 0, hidden from task assignment
  //              and every staff dropdown (Disable / Enable buttons).
  const TAB_KEYS = ["all", "active", "inactive", "disabled"];
  const subTabCtx = useContext(SettingsSubTabContext);
  const urlTab = adminCompanyId ? initialTab : subTabCtx?.subTab;
  const [statusTab, setStatusTabState] = useState(
    TAB_KEYS.includes(String(urlTab || "").toLowerCase()) ? String(urlTab).toLowerCase() : "active"
  );
  useEffect(() => {
    if (adminCompanyId) return;
    const t = String(subTabCtx?.subTab || "").toLowerCase();
    if (TAB_KEYS.includes(t) && t !== statusTab) setStatusTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTabCtx?.subTab]);
  const setStatusTab = (key) => {
    setStatusTabState(key);
    if (adminCompanyId) {
      window.history.replaceState(null, "", `/company-details/${adminCompanyId}/Users/${key}`);
    } else if (subTabCtx?.setSubTab) {
      subTabCtx.setSubTab(key);
    }
  };
  // Back End: Company Details rewrites the URL to /Users when it opens the
  // tab (after this effect runs), so put the sub tab back on the next tick.
  useEffect(() => {
    if (!adminCompanyId) return undefined;
    const t = setTimeout(
      () => window.history.replaceState(null, "", `/company-details/${adminCompanyId}/Users/${statusTab}`),
      0
    );
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminCompanyId]);
  const filterNoLicence = false;
  const [filterLocked, setFilterLocked] = useState(false);
  // Change #100465 - filters: licence product, security role, last login.
  const [filterProduct, setFilterProduct] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterLogin, setFilterLogin] = useState("");
  const productOptions = useMemo(() => {
    const map = {};
    (teamList || []).forEach((r) =>
      (r?.subscriptionlicence_list || []).forEach((l) => {
        map[String(l.SubscriptionProduct_Id)] = l.SubscriptionProduct_Name || `Product #${l.SubscriptionProduct_Id}`;
      })
    );
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [teamList]);
  const roleOptions = useMemo(() => {
    const map = {};
    (teamList || []).forEach((r) =>
      (r?.securityauthrole_list || []).forEach((x) => {
        map[String(x.SecurityAuthRole_Id)] = x.SecurityAuthRole_Name || `Role #${x.SecurityAuthRole_Id}`;
      })
    );
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [teamList]);
  const daysSince = (v) => {
    if (!v) return null;
    const d = new Date(String(v).replace(" ", "T"));
    return Number.isNaN(d.getTime()) ? null : Math.floor((Date.now() - d.getTime()) / 86400000);
  };
  const matchesFilters = (row) => {
    if (filterProduct) {
      if (filterProduct === "__none") {
        if ((row?.subscriptionlicence_list || []).length) return false;
      } else if (!(row?.subscriptionlicence_list || []).some((l) => String(l.SubscriptionProduct_Id) === filterProduct)) {
        return false;
      }
    }
    if (filterRole) {
      if (filterRole === "__none") {
        if ((row?.securityauthrole_list || []).length) return false;
      } else if (!(row?.securityauthrole_list || []).some((x) => String(x.SecurityAuthRole_Id) === filterRole)) {
        return false;
      }
    }
    if (filterLogin) {
      const days = daysSince(row?.CompanyToUser_LastLoginDateTime);
      if (filterLogin === "never" && days !== null) return false;
      if (filterLogin === "7" && (days === null || days > 7)) return false;
      if (filterLogin === "30plus" && days !== null && days < 30) return false;
      if (filterLogin === "90plus" && days !== null && days < 90) return false;
    }
    return true;
  };
  const filtersOn = !!(filterProduct || filterRole || filterLogin || filterLocked);
  const clearFilters = () => {
    setFilterProduct("");
    setFilterRole("");
    setFilterLogin("");
    setFilterLocked(false);
  };
  const groupOf = (row) => {
    if (row?.Staff_FlagActive !== null && row?.Staff_FlagActive !== undefined &&
      Number(row.Staff_FlagActive) === 0)
      return "disabled";
    return liveLicences(row).length === 0 ? "inactive" : "active";
  };
  const inTab = (row) => statusTab === "all" || groupOf(row) === statusTab;
  const isLockedRow = (row) => Number(row?.CompanyToUser_UserLockedFlag) === 1;
  const hasNoLicence = (row) => liveLicences(row).length === 0;
  const groupCounts = useMemo(() => {
    const counts = { all: 0, active: 0, inactive: 0, disabled: 0 };
    (searchResults || []).forEach((row) => {
      counts[groupOf(row)] += 1;
      counts.all += 1;
    });
    return counts;
  }, [searchResults]);
  const tabRows = useMemo(
    () =>
      (searchResults || []).filter(
        (row) =>
          inTab(row) &&
          (!filterNoLicence || hasNoLicence(row)) &&
          (!filterLocked || isLockedRow(row)) &&
          matchesFilters(row)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResults, statusTab, filterLocked, filterProduct, filterRole, filterLogin]
  );
  const filterCounts = useMemo(() => {
    const rows = (searchResults || []).filter(inTab);
    return { noLicence: rows.filter(hasNoLicence).length, locked: rows.filter(isLockedRow).length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, statusTab]);
  selectableRef.current = tabRows;
  const selectedRows = Object.values(selected);
  // Same people as the Inactive tab (it used to leave locked people out, so
  // the bar and the tab disagreed).
  const unlicensedCount = (teamList || []).filter((r) => groupOf(r) === "inactive").length;

  const STATUS_TABS = [
    { key: "all", label: "All", badge: "bg-light text-body" },
    { key: "active", label: "Active", badge: "bg-success-subtle text-success" },
    { key: "inactive", label: "Inactive", badge: "bg-warning-subtle text-warning" },
    { key: "disabled", label: "Disabled", badge: "bg-secondary-subtle text-secondary" },
  ];

  return (
    <SettingsPanel
      title="Users"
      description="Everyone with access to this company — lock or unlock their access"
    >
      {signInLogTarget && (
        <SignInLogModal
          user={signInLogTarget}
          onClose={() => setSignInLogTarget(null)}
        />
      )}
      <Row>
        <Col lg={12}>
          <Card id="usersList">
            <CardHeader className="border-0">
              <Row className="g-4 align-items-center">
                <Col sm={3}>
                  <div className="search-box">
                    <Input
                      type="search"
                      className="form-control search"
                      placeholder="Search for..."
                      onChange={(e) => setSearch(e.target.value)}
                      value={search}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </Col>
                <div className="col-sm-auto ms-auto">
                  <div className="hstack gap-3">
                    {/* Change #100337 */}
                    {(
                    <div
                      className="form-check form-switch mb-0"
                      title="Also show each person's landing page, menu and dashboard"
                    >
                      <Input
                        type="switch"
                        id="usersTechnicalView"
                        className="form-check-input"
                        checked={techView}
                        onChange={(e) => toggleTechView(e.target.checked)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="usersTechnicalView"
                      >
                        Technical view
                      </label>
                    </div>
                    )}
                    {selectedRows.length > 0 && (
                      <Button color="primary" onClick={() => setBulkOpen(true)}>
                        <i className="ri-key-2-line align-bottom me-1"></i>
                        Assign licences ({selectedRows.length})
                      </Button>
                    )}
                    {!adminCompanyId && (
                      <Button
                        color="success"
                        onClick={() => setModalUserCreateEdit(true)}
                      >
                        <i className="ri-user-add-line align-bottom me-1"></i>
                        Add New User
                      </Button>
                    )}
                  </div>
                </div>
              </Row>
              {unlicensedCount > 0 && (
                <div className="alert alert-warning d-flex align-items-center gap-2 py-2 px-3 mt-3 mb-0 fs-13">
                  <i className="ri-error-warning-line fs-16"></i>
                  <div>
                    {unlicensedCount} {unlicensedCount === 1 ? "person has" : "people have"} no working licence
                    (none, or one that has lapsed because its renewal was not paid). They do not count towards your
                    usage, but need a working licence to use the system. Tick them and choose{" "}
                    <strong>Assign licences</strong>, or renew the lapsed ones in Manage Licence.
                  </div>
                </div>
              )}
              <div className="d-flex flex-wrap align-items-end gap-2 mt-3">
              <ul className="nav nav-tabs nav-tabs-custom card-header-tabs border-bottom-0 flex-grow-1">
                {STATUS_TABS.map((t) => (
                  <li className="nav-item" key={t.key}>
                    <button
                      type="button"
                      className={`nav-link fw-semibold${
                        statusTab === t.key ? " active" : ""
                      }`}
                      onClick={() => setStatusTab(t.key)}
                    >
                      {t.label}
                      <span className={`badge ms-2 ${t.badge}`}>
                        {groupCounts[t.key]}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="hstack gap-2 pb-2">
                <button
                  type="button"
                  className={`btn btn-sm ${filterLocked ? "btn-danger" : "btn-soft-danger"}`}
                  onClick={() => setFilterLocked((v) => !v)}
                  title="Only locked people"
                >
                  <i className="ri-lock-line align-bottom me-1"></i>
                  Locked ({filterCounts.locked})
                </button>
              </div>
              </div>
              <Row className="g-2 align-items-end mt-1">
                <Col sm={6} md={3}>
                  <label className="form-label mb-1 fs-12 text-muted">Licence</label>
                  <Input type="select" bsSize="sm" value={filterProduct} onChange={(e) => setFilterProduct(e.target.value)}>
                    <option value="">Any licence</option>
                    <option value="__none">No licence</option>
                    {productOptions.map(([id, name]) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col sm={6} md={3}>
                  <label className="form-label mb-1 fs-12 text-muted">Security role</label>
                  <Input type="select" bsSize="sm" value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                    <option value="">Any role</option>
                    <option value="__none">No roles</option>
                    {roleOptions.map(([id, name]) => (
                      <option key={id} value={id}>
                        {name}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col sm={6} md={3}>
                  <label className="form-label mb-1 fs-12 text-muted">Last login</label>
                  <Input type="select" bsSize="sm" value={filterLogin} onChange={(e) => setFilterLogin(e.target.value)}>
                    <option value="">Any time</option>
                    <option value="7">In the last 7 days</option>
                    <option value="30plus">Not for 30+ days (or never)</option>
                    <option value="90plus">Not for 90+ days (or never)</option>
                    <option value="never">Never logged in</option>
                  </Input>
                </Col>
                <Col sm={6} md={3} className="text-md-end">
                  {filtersOn && (
                    <button type="button" className="btn btn-sm btn-light" onClick={clearFilters}>
                      <i className="ri-close-line align-bottom me-1"></i>Clear filters ({tabRows.length} shown)
                    </button>
                  )}
                </Col>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              {statusTab === "disabled" && (
                <div className="text-muted fs-13 mb-2">
                  People who have left. They are hidden from task assignment and every staff list; their history
                  is kept. Enable someone to bring them back.
                </div>
              )}
              {teamListLoading ? (
                <LoadingSpinner />
              ) : (
                <div>
                  {!isEmpty(teamList) ? (
                    <TableContainer
                      columns={columns}
                      data={tabRows}
                      // Change #100330 - A-Z by name on load. The table's
                      // default sorts on an "Id" column this list does not
                      // have, so it came back in API order.
                      initialSortBy={[{ id: "User_UserFullName", desc: false }]}
                      isGlobalFilter={false}
                      isAddUserList={false}
                      customPageSize={10}
                      className="custom-header-css"
                      divClass="table-responsive table-card mb-0"
                      tableClass="align-middle table-nowrap"
                      theadClass="text-muted"
                      isLeadsFilter={false}
                    />
                  ) : (
                    <NoDataContent
                      titleText={`You have no users yet.`}
                      paragraphText={`Get started by inviting your first user.`}
                      useButton={false}
                    />
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {modalUserCreateEdit && (
        <CreateEditCompanyUserLicenses
          isEdit={false}
          modal={modalUserCreateEdit}
          setModal={setModalUserCreateEdit}
          toggle={() => setModalUserCreateEdit(!modalUserCreateEdit)}
          updateData={null}
        />
      )}

      <BulkAssignLicence
        adminCompanyId={adminCompanyId}
        open={bulkOpen}
        people={selectedRows}
        onClose={() => setBulkOpen(false)}
        onDone={() => {
          setBulkOpen(false);
          setSelected({});
          reloadList();
        }}
      />

      <BulkAssignLicence
        adminCompanyId={adminCompanyId}
        open={!!assignTarget}
        people={assignTarget ? [assignTarget] : []}
        onClose={() => setAssignTarget(null)}
        onDone={() => {
          setAssignTarget(null);
          reloadList();
        }}
      />

      <Modal isOpen={!!activeTarget} toggle={() => !activeSaving && setActiveTarget(null)} centered>
        <ModalHeader toggle={() => !activeSaving && setActiveTarget(null)}>
          {activeTarget?.active ? "Enable user" : "Disable user"}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            {activeTarget?.active ? "Enable " : "Disable "}
            <strong>{activeTarget?.row?.User_UserFullName || activeTarget?.row?.User_Email}</strong>?
          </p>
          <div className="text-muted fs-13">
            {activeTarget?.active
              ? "They appear in task assignment and staff lists again. Their old licences are not given back: assign one so they can use the system."
              : "Use this when someone has left (to stop someone temporarily, use Lock instead). They disappear from task assignment and every staff dropdown and move to the Disabled tab. Their history is kept."}
          </div>
          {!activeTarget?.active && (activeTarget?.row?.subscriptionlicence_list || []).length > 0 && (
            <div className="alert alert-warning fs-13 mt-3 mb-0">
              <div className="fw-semibold mb-1">
                {(activeTarget.row.subscriptionlicence_list || []).length === 1
                  ? "This licence will be unassigned:"
                  : `These ${activeTarget.row.subscriptionlicence_list.length} licences will be unassigned:`}
              </div>
              <ul className="mb-1 ps-3">
                {activeTarget.row.subscriptionlicence_list.map((l) => (
                  <li key={l.SubscriptionLicence_Id}>{l.SubscriptionProduct_Name || `Licence #${l.SubscriptionLicence_Id}`}</li>
                ))}
              </ul>
              They become spares you can give to someone else. They are not cancelled and billing does not change.
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={activeSaving} onClick={() => setActiveTarget(null)}>
            Cancel
          </Button>
          <Button color={activeTarget?.active ? "success" : "secondary"} disabled={activeSaving} onClick={confirmActive}>
            {activeSaving ? "Working..." : activeTarget?.active ? "Enable" : "Disable"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!unassignTarget} toggle={() => !unassigning && setUnassignTarget(null)} centered>
        <ModalHeader toggle={() => !unassigning && setUnassignTarget(null)}>Unassign licence</ModalHeader>
        <ModalBody>
          <p className="mb-2">
            Take{" "}
            <strong>{unassignTarget?.licence?.SubscriptionProduct_Name || "this licence"}</strong> away from{" "}
            <strong>{unassignTarget?.row?.User_UserFullName || unassignTarget?.row?.User_Email}</strong>?
          </p>
          <div className="text-muted fs-13">
            The licence stays on your subscription as a spare, so you can give it to someone else. It is not
            cancelled and billing does not change. Without a working licence they move to Inactive and cannot use
            the system, but stay in staff lists.
          </div>
          {(unassignTarget?.row?.subscriptionlicence_list || []).length <= 1 && (
            <div className="border rounded-3 p-2 mt-3 bg-light">
              <div className="fw-semibold fs-13 mb-1">This is their last licence. Do you also want to disable them?</div>
              <div className="form-check mb-0">
                <Input
                  type="radio"
                  className="form-check-input"
                  name="unassign-left"
                  id="unassign-left-no"
                  checked={!unassignLeft}
                  onChange={() => setUnassignLeft(false)}
                />
                <label className="form-check-label fs-13" htmlFor="unassign-left-no">
                  No, keep them (they move to Inactive and stay in staff lists)
                </label>
              </div>
              <div className="form-check mb-0">
                <Input
                  type="radio"
                  className="form-check-input"
                  name="unassign-left"
                  id="unassign-left-yes"
                  checked={unassignLeft}
                  onChange={() => setUnassignLeft(true)}
                />
                <label className="form-check-label fs-13" htmlFor="unassign-left-yes">
                  Yes, they have left: disable them (hidden from task assignment and staff lists)
                </label>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={unassigning} onClick={() => setUnassignTarget(null)}>
            Cancel
          </Button>
          <Button color="danger" disabled={unassigning} onClick={confirmUnassign}>
            {unassigning ? "Working..." : unassignLeft ? "Unassign and disable" : "Unassign"}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!resetTarget} toggle={closeResetModal} centered>
        <ModalHeader toggle={closeResetModal}>Reset password</ModalHeader>
        <ModalBody>
          <p className="mb-3">
            This emails{" "}
            <strong>
              {resetTarget?.User_UserFullName || resetTarget?.User_Email}
            </strong>{" "}
            at <strong>{resetTarget?.User_Email}</strong> a one-time link to
            choose a new password. Their current password keeps working until
            they use it, and the link expires in 24 hours.
          </p>
          {/* Change #100329 - a "set a password" option was built and withdrawn,
              and the link is no longer shown here either: a login is shared
              across companies. Say why so the question does not come back. */}
          <div className="alert alert-info d-flex gap-2 small py-2 mb-0">
            <i className="ri-shield-keyhole-line fs-16 lh-1"></i>
            <div>
              <strong>
                Why can't I set their password or copy the link?
              </strong>{" "}
              A CRMit login can belong to more than one company, and its
              password opens all of them. So that nobody in one company can get
              into someone's other companies, only the person themselves can
              choose their password, from the link sent to their own email.
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeResetModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={resetLoading}
            onClick={handleConfirmReset}
          >
            {resetLoading ? "Working..." : "Send reset email"}
          </Button>
        </ModalFooter>
      </Modal>
    </SettingsPanel>
  );
}
